import { motion, MotionConfig } from "framer-motion";
import React, { useEffect, useRef } from "react";

const arrivingTransition = {
    default: { type: "spring", stiffness: 500, damping: 30 },
    backgroundColor: { duration: 1.6, ease: "easeOut" },
} as const;

const arriving = {
    opacity: 0,
    y: -8,
    backgroundColor: "rgba(34, 197, 94, 0.18)",
};

const settled = {
    opacity: 1,
    y: 0,
    backgroundColor: "rgba(34, 197, 94, 0)",
};

const departing = {
    opacity: 0,
    y: -8,
    transition: { duration: 0.18 },
};

interface ArrivingListItemProps {
    isNew: boolean;
    className: string;
    children: React.ReactNode;
}

export const ArrivingListItem: React.FC<ArrivingListItemProps> = props => (
    <MotionConfig reducedMotion="user">
        <motion.li
            layout
            className={props.className}
            initial={props.isNew ? arriving : false}
            animate={settled}
            transition={arrivingTransition}>
            {props.children}
        </motion.li>
    </MotionConfig>
);

const completionFlash = {
    open: {},
    done: { backgroundColor: ["rgba(34, 197, 94, 0.25)", "rgba(34, 197, 94, 0)"] },
};

const completionTransition = {
    default: { type: "spring", stiffness: 500, damping: 30 },
    backgroundColor: { duration: 1.4, ease: "easeOut" },
} as const;

const CompletionFlash: React.FC<{ isDone: boolean }> = ({ isDone }) => (
    <motion.span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        variants={completionFlash}
        initial={false}
        animate={isDone ? "done" : "open"}
        transition={completionTransition}
    />
);

interface CompletingProps {
    isDone: boolean;
    className: string;
    children: React.ReactNode;
}

export const CompletingListItem: React.FC<CompletingProps> = props => (
    <MotionConfig reducedMotion="user">
        <motion.li
            layout
            className={`relative ${props.className}`}
            initial={false}
            exit={departing}
            transition={completionTransition}>
            <CompletionFlash isDone={props.isDone} />
            {props.children}
        </motion.li>
    </MotionConfig>
);

export const CompletingBlock: React.FC<CompletingProps> = props => (
    <MotionConfig reducedMotion="user">
        <div className={`relative ${props.className}`}>
            <CompletionFlash isDone={props.isDone} />
            {props.children}
        </div>
    </MotionConfig>
);

const nothingArrived: ReadonlySet<string> = new Set();

export const useArrivals = (ids: string[] | null): ReadonlySet<string> => {
    const seen = useRef<Set<string> | null>(null);
    const alreadySeen = seen.current;
    useEffect(() => {
        if (ids === null) {
            seen.current = null;
            return;
        }
        if (seen.current === null) {
            seen.current = new Set(ids);
            return;
        }
        for (const id of ids) {
            seen.current.add(id);
        }
    }, [ids]);
    if (ids === null || alreadySeen === null) {
        return nothingArrived;
    }
    const arrivals = ids.filter(id => !alreadySeen.has(id));
    return arrivals.length === 0 ? nothingArrived : new Set(arrivals);
};
